import { ChatState, Conversation, ChatMessage } from '../schemas/chat';

// Action types
export type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CONVERSATIONS'; payload: Conversation[] }
  | { type: 'ADD_CONVERSATION'; payload: Conversation }
  | { type: 'UPDATE_CONVERSATION'; payload: Conversation }
  | { type: 'DELETE_CONVERSATION'; payload: string }
  | { type: 'SET_CURRENT_CONVERSATION'; payload: string | null }
  | { type: 'ADD_MESSAGE'; payload: { conversationId: string; message: ChatMessage } }
  | {
      type: 'UPDATE_MESSAGE';
      payload: { conversationId: string; messageId: string; message: Partial<ChatMessage> };
    }
  | { type: 'DELETE_MESSAGE'; payload: { conversationId: string; messageId: string } }
  | {
      type: 'SET_STREAMING';
      payload: { conversationId: string; messageId: string; isStreaming: boolean };
    }
  | { type: 'CLEAR_STATE' };

// Initial state
export const initialState: ChatState = {
  conversations: [],
  currentConversation: null,
  currentConversationId: null,
  isLoading: false,
  error: null,
  streamingConversationId: null,
  streamingMessageId: null,
};

// Reducer
export const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_CONVERSATIONS':
      // Merge new conversations with existing ones, preserving messages that were already loaded
      const mergedConversations = action.payload.map(newConv => {
        const existingConv = state.conversations.find(c => c.id === newConv.id);
        // If conversation exists and has messages, preserve the messages
        if (existingConv && existingConv.messages && existingConv.messages.length > 0) {
          return {
            ...newConv,
            messages: existingConv.messages,
          };
        }
        return newConv;
      });
      return { ...state, conversations: mergedConversations };

    case 'ADD_CONVERSATION':
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
      };

    case 'UPDATE_CONVERSATION':
      const existingConv = state.conversations.find(c => c.id === action.payload.id);
      const newState = {
        ...state,
        conversations: existingConv
          ? // Update existing conversation
            state.conversations.map(conv =>
              conv.id === action.payload.id
                ? {
                    ...conv,
                    ...action.payload,
                    // Merge messages: if payload has non-empty messages array, use it
                    // otherwise preserve existing messages (for optimistic updates)
                    messages:
                      Array.isArray(action.payload.messages) && action.payload.messages.length > 0
                        ? action.payload.messages
                        : conv.messages || [],
                  }
                : conv
            )
          : // Add new conversation (upsert)
            [...state.conversations, action.payload],
      };
      return newState;

    case 'DELETE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.filter(conv => conv.id !== action.payload),
        currentConversationId:
          state.currentConversationId === action.payload ? null : state.currentConversationId,
      };

    case 'SET_CURRENT_CONVERSATION':
      return { ...state, currentConversationId: action.payload };

    case 'ADD_MESSAGE': {
      const { conversationId, message } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.id === conversationId
            ? {
                ...conv,
                messages: [...(conv.messages || []), message],
                updatedAt: new Date().toISOString(),
              }
            : conv
        ),
      };
    }

    case 'UPDATE_MESSAGE': {
      const { conversationId, messageId, message } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.id === conversationId
            ? {
                ...conv,
                messages: (conv.messages || []).map(msg =>
                  msg.id === messageId ? { ...msg, ...message } : msg
                ),
                updatedAt: new Date().toISOString(),
              }
            : conv
        ),
      };
    }

    case 'DELETE_MESSAGE': {
      const { conversationId, messageId } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.id === conversationId
            ? {
                ...conv,
                messages: (conv.messages || []).filter(msg => msg.id !== messageId),
                updatedAt: new Date().toISOString(),
              }
            : conv
        ),
      };
    }

    case 'SET_STREAMING':
      return {
        ...state,
        streamingConversationId: action.payload.isStreaming ? action.payload.conversationId : null,
        streamingMessageId: action.payload.isStreaming ? action.payload.messageId : null,
      };

    case 'CLEAR_STATE':
      return initialState;

    default:
      return state;
  }
};
